/**
 * Storage helper functions to extract values from composite storage objects
 * This reduces redundant storage keys by using a single source of truth
 */

/**
 * Extract user name from currentUser object
 * Replaces direct localStorage.getItem('userName') calls
 * 
 * @returns User's name or null if not available
 */
export function getUserName(): string | null {
  if (typeof window === 'undefined') return null
  
  const currentUser = localStorage.getItem('currentUser')
  if (currentUser) {
    try {
      const user = JSON.parse(currentUser)
      return user.name || null
    } catch {
      return null
    }
  }
  return null
}

/**
 * Extract user email from currentUser object (same key as dashboard / sign-in flow).
 */
export function getUserEmail(): string | null {
  if (typeof window === 'undefined') return null

  const currentUser = localStorage.getItem('currentUser')
  if (currentUser) {
    try {
      const user = JSON.parse(currentUser) as { email?: string }
      return typeof user.email === 'string' && user.email.length > 0
        ? user.email
        : null
    } catch {
      return null
    }
  }
  return null
}

/**
 * Extract interview ID from currentInterview object
 * Replaces direct sessionStorage.getItem('currentInterviewId') calls
 * 
 * @returns Interview ID as UUID string (after Phase 2.2 migration) or null if not available
 */
export function getCurrentInterviewId(): string | null {
  if (typeof window === 'undefined') return null
  
  const currentInterview = sessionStorage.getItem('currentInterview')
  if (currentInterview) {
    try {
      const interview = JSON.parse(currentInterview)
      // Return UUID string directly (after Phase 2.2 migration)
      return interview.id ? interview.id.toString() : null
    } catch {
      return null
    }
  }
  return null
}

/**
 * Extract duration minutes from currentInterview object
 * Replaces direct sessionStorage.getItem('durationMinutes') calls
 * Falls back to 45 if not found
 * 
 * @returns Duration in minutes (defaults to 45)
 */
export function getDurationMinutes(): number {
  if (typeof window === 'undefined') return 45
  
  const currentInterview = sessionStorage.getItem('currentInterview')
  if (currentInterview) {
    try {
      const interview = JSON.parse(currentInterview)
      const duration = interview.startInterviewData?.duration_minutes
      return duration ? parseInt(duration.toString()) : 45
    } catch {
      return 45
    }
  }
  return 45
}

/** Legacy thank-you storage key (single key, not scoped - kept for backward compat read fallback) */
const THANK_YOU_MESSAGE_LEGACY_KEY = 'thankYouMessage'

/**
 * Get sessionStorage key for thank-you message, scoped by interview and attempt.
 * Prevents cross-interview cache contamination (e.g. JD message used for resume interview).
 */
export function getThankYouMessageKey(interviewId: string | number | null, attemptNumber: number | null): string {
  if (interviewId != null && attemptNumber != null) {
    return `thankYouMessage_${interviewId}_${attemptNumber}`
  }
  return THANK_YOU_MESSAGE_LEGACY_KEY
}

/**
 * Get thank-you message from sessionStorage. Tries scoped key first, then legacy key.
 */
export function getThankYouMessage(interviewId: string | number | null, attemptNumber: number | null): string | null {
  if (typeof window === 'undefined') return null
  const scopedKey = getThankYouMessageKey(interviewId, attemptNumber)
  const scoped = sessionStorage.getItem(scopedKey)
  if (scoped) return scoped
  if (interviewId != null && attemptNumber != null) {
    return sessionStorage.getItem(THANK_YOU_MESSAGE_LEGACY_KEY)
  }
  return null
}

/**
 * Store thank-you message in sessionStorage under scoped key.
 */
export function setThankYouMessage(interviewId: string | number | null, attemptNumber: number | null, data: string): void {
  if (typeof window === 'undefined') return
  const key = getThankYouMessageKey(interviewId, attemptNumber)
  sessionStorage.setItem(key, data)
}

/**
 * Remove thank-you message from sessionStorage. Clears both scoped key (when ids provided) and legacy key.
 */
export function removeThankYouMessage(interviewId?: string | number | null, attemptNumber?: number | null): void {
  if (typeof window === 'undefined') return
  if (interviewId != null && attemptNumber != null) {
    sessionStorage.removeItem(getThankYouMessageKey(interviewId, attemptNumber))
  }
  sessionStorage.removeItem(THANK_YOU_MESSAGE_LEGACY_KEY)
}

/**
 * Resolve interview id and attempt for analytics / navigation after completion.
 * Prefer `completion_*` fields embedded in stored analysis (set when clearing in-progress session).
 */
export function readInterviewCompletionFromAnalysisPayload(parsed: unknown): {
  interviewId: string | null
  attemptNumberStr: string | null
} {
  let completionInterviewId: string | null = null
  let completionAttemptStr: string | null = null
  if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const o = parsed as Record<string, unknown>
    const cid = o.completion_interview_id
    if (typeof cid === 'string' && cid.length > 0) {
      completionInterviewId = cid
    } else if (typeof cid === 'number' && Number.isFinite(cid)) {
      completionInterviewId = String(cid)
    }
    const an = o.completion_attempt_number
    if (an != null && an !== '') {
      const n = typeof an === 'number' ? an : parseInt(String(an), 10)
      if (!Number.isNaN(n)) completionAttemptStr = String(n)
    }
  }
  const interviewId = completionInterviewId ?? getCurrentInterviewId()
  const attemptNumberStr =
    completionAttemptStr ??
    (typeof window !== 'undefined' ? sessionStorage.getItem('currentAttemptNumber') : null)
  return { interviewId, attemptNumberStr }
}

